/**
 * Synth Behavior Editor
 * UI for editing oscillator-based synth behavior with test playback controls
 */

import React, { useCallback } from 'react';
import type { SynthData, SynthWaveform } from 'trace-viewer/behaviors';
import { Synth } from 'trace-viewer/behaviors';

// ============================================================================
// MIDI helpers
// ============================================================================

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

function midiNoteName(note: number): string {
    const octave = Math.floor(note / 12) - 1;
    const name = NOTE_NAMES[note % 12];
    return `${name}${octave}`;
}

// ============================================================================
// Styles
// ============================================================================

const styles = {
    input: {
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        outline: 'none',
    } as React.CSSProperties,
    label: {
        fontSize: '12px',
        color: '#aaa',
        minWidth: '100px',
        flexShrink: 0,
    } as React.CSSProperties,
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    } as React.CSSProperties,
    sectionLabel: {
        fontSize: '11px',
        color: '#888',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        marginTop: '12px',
        marginBottom: '6px',
    } as React.CSSProperties,
};

const WAVEFORMS: SynthWaveform[] = ['sine', 'square', 'sawtooth', 'triangle'];

// ============================================================================
// Component
// ============================================================================

export interface SynthEditorProps {
    data: SynthData;
    behavior: Synth;
    onChange: (data: SynthData) => void;
}

export const SynthEditor: React.FC<SynthEditorProps> = ({ data, behavior, onChange }) => {

    const handleChange = useCallback((field: keyof SynthData, value: unknown) => {
        onChange({ ...data, [field]: value });
    }, [data, onChange]);

    const handleTest = useCallback(() => {
        if (data.frequency !== undefined) {
            behavior.playFrequency(data.frequency);
        } else {
            behavior.playNote(data.note ?? 60);
        }
    }, [behavior, data.frequency, data.note]);

    const handleStop = useCallback(() => {
        behavior.stop();
    }, [behavior]);

    const note = data.note ?? 60;
    const spatial = data.spatial ?? true;

    return (
        <div>
            {/* Preview Controls */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Preview</span>
                <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                        onClick={handleTest}
                        style={{
                            padding: '4px 12px',
                            backgroundColor: '#4caf50',
                            border: 'none',
                            borderRadius: '3px',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '11px',
                        }}
                    >
                        Test
                    </button>
                    <button
                        onClick={handleStop}
                        style={{
                            padding: '4px 12px',
                            backgroundColor: '#555',
                            border: 'none',
                            borderRadius: '3px',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '11px',
                        }}
                    >
                        Stop
                    </button>
                </div>
            </div>

            {/* Waveform */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Waveform</span>
                <select
                    value={data.waveform ?? 'sine'}
                    onChange={(e) => handleChange('waveform', e.target.value)}
                    style={{ ...styles.input, flex: 1, cursor: 'pointer' }}
                >
                    {WAVEFORMS.map((w) => (
                        <option key={w} value={w}>{w}</option>
                    ))}
                </select>
            </div>

            {/* Note */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Note</span>
                <input
                    type="number"
                    value={note}
                    min={0}
                    max={127}
                    step={1}
                    onChange={(e) => handleChange('note', parseInt(e.target.value) || 0)}
                    style={{ ...styles.input, width: '60px' }}
                />
                <span style={{ fontSize: '12px', color: '#9cb' }}>{midiNoteName(note)}</span>
            </div>

            {/* Frequency override */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Frequency (Hz)</span>
                <input
                    type="number"
                    value={data.frequency ?? ''}
                    min={20}
                    max={20000}
                    step={1}
                    placeholder="auto"
                    onChange={(e) => {
                        const val = e.target.value;
                        handleChange('frequency', val ? parseFloat(val) : undefined);
                    }}
                    style={{ ...styles.input, width: '80px' }}
                />
            </div>

            {/* Volume */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Volume</span>
                <input
                    type="range"
                    value={data.volume ?? 0.5}
                    min={0}
                    max={1}
                    step={0.05}
                    onChange={(e) => handleChange('volume', parseFloat(e.target.value))}
                    style={{ flex: 1 }}
                />
                <span style={{ fontSize: '11px', color: '#888', width: '32px', textAlign: 'right' }}>
                    {((data.volume ?? 0.5) * 100).toFixed(0)}%
                </span>
            </div>

            {/* Duration */}
            <div style={styles.fieldRow}>
                <span style={styles.label}>Duration (s)</span>
                <input
                    type="number"
                    value={data.duration ?? 0.5}
                    min={0}
                    max={30}
                    step={0.1}
                    onChange={(e) => handleChange('duration', parseFloat(e.target.value))}
                    style={{ ...styles.input, width: '60px' }}
                />
            </div>

            {/* ADSR Envelope */}
            <div style={styles.sectionLabel}>Envelope (ADSR)</div>
            <div style={styles.fieldRow}>
                <span style={styles.label}>Attack (s)</span>
                <input
                    type="number"
                    value={data.attack ?? 0.01}
                    min={0}
                    max={5}
                    step={0.01}
                    onChange={(e) => handleChange('attack', parseFloat(e.target.value))}
                    style={{ ...styles.input, width: '60px' }}
                />
            </div>
            <div style={styles.fieldRow}>
                <span style={styles.label}>Decay (s)</span>
                <input
                    type="number"
                    value={data.decay ?? 0.1}
                    min={0}
                    max={5}
                    step={0.01}
                    onChange={(e) => handleChange('decay', parseFloat(e.target.value))}
                    style={{ ...styles.input, width: '60px' }}
                />
            </div>
            <div style={styles.fieldRow}>
                <span style={styles.label}>Sustain</span>
                <input
                    type="range"
                    value={data.sustain ?? 0.7}
                    min={0}
                    max={1}
                    step={0.05}
                    onChange={(e) => handleChange('sustain', parseFloat(e.target.value))}
                    style={{ flex: 1 }}
                />
                <span style={{ fontSize: '11px', color: '#888', width: '32px', textAlign: 'right' }}>
                    {((data.sustain ?? 0.7) * 100).toFixed(0)}%
                </span>
            </div>
            <div style={styles.fieldRow}>
                <span style={styles.label}>Release (s)</span>
                <input
                    type="number"
                    value={data.release ?? 0.3}
                    min={0}
                    max={10}
                    step={0.01}
                    onChange={(e) => handleChange('release', parseFloat(e.target.value))}
                    style={{ ...styles.input, width: '60px' }}
                />
            </div>

            {/* Spatial Audio */}
            <div style={styles.sectionLabel}>Spatial Audio</div>
            <div style={styles.fieldRow}>
                <span style={styles.label}>Spatial</span>
                <input
                    type="checkbox"
                    checked={spatial}
                    onChange={(e) => handleChange('spatial', e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
            </div>
            {spatial && (
                <>
                    <div style={styles.fieldRow}>
                        <span style={styles.label}>Min Distance</span>
                        <input
                            type="number"
                            value={data.minDistance ?? 1}
                            min={0}
                            step={0.5}
                            onChange={(e) => handleChange('minDistance', parseFloat(e.target.value))}
                            style={{ ...styles.input, width: '60px' }}
                        />
                    </div>
                    <div style={styles.fieldRow}>
                        <span style={styles.label}>Max Distance</span>
                        <input
                            type="number"
                            value={data.maxDistance ?? 50}
                            min={0}
                            step={1}
                            onChange={(e) => handleChange('maxDistance', parseFloat(e.target.value))}
                            style={{ ...styles.input, width: '60px' }}
                        />
                    </div>
                    <div style={styles.fieldRow}>
                        <span style={styles.label}>Rolloff</span>
                        <input
                            type="number"
                            value={data.rolloff ?? 1}
                            min={0}
                            max={10}
                            step={0.1}
                            onChange={(e) => handleChange('rolloff', parseFloat(e.target.value))}
                            style={{ ...styles.input, width: '60px' }}
                        />
                    </div>
                </>
            )}

            {/* Play on Start */}
            <div style={{ ...styles.fieldRow, marginTop: '12px' }}>
                <span style={styles.label}>Play on Start</span>
                <input
                    type="checkbox"
                    checked={data.playOnStart ?? false}
                    onChange={(e) => handleChange('playOnStart', e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
            </div>
        </div>
    );
};
